import { CONTACT_ENTRIES, CONTACT_PLATFORMS, type ContactPlatform } from '@fluenceur/types';
import { parseAsBoolean, parseAsString, parseAsStringLiteral } from 'nuqs';
import type { ContactView } from './contact-views';
import { CONTACT_VIEWS } from './contact-views';

/**
 * URL state for the unified contact drawer: the nuqs parsers and the helpers
 * that open, drill into and close it. The keys, platforms, entries and the
 * Discovery route live in `@fluenceur/types`, because links from outside the
 * dashboard (the MCP server) address the drawer with the same contract.
 */
export { CONTACT_DRAWER_QUERY_KEYS, type ContactPlatform } from '@fluenceur/types';

export type ContactEntry = (typeof CONTACT_ENTRIES)[number];

/**
 * nuqs parser map for the contact drawer. Consume with `useQueryStates` to read
 * the whole tuple at once, or pluck a single key with `useQueryState`.
 */
export const contactDrawerParsers = {
  /** Opaque id (affiliate user id / creator username / identity id). Presence opens the drawer. */
  contactId: parseAsString,
  /** Discriminant describing how the contact was opened (drives identity resolution). */
  contactEntry: parseAsStringLiteral(CONTACT_ENTRIES),
  /** Active detail view in the right panel. */
  contactView: parseAsStringLiteral(CONTACT_VIEWS).withDefault('profile'),
  /** Selected social platform for the profile view + IG/TikTok toggle. */
  contactPlatform: parseAsStringLiteral(CONTACT_PLATFORMS).withDefault('ig'),
  /** Optional deep-link to a specific collaboration row (e.g. a program id). */
  contactCollab: parseAsString,
  /** Optional materialized deliverable drill-in within a collaboration. */
  contactDeliverable: parseAsString,
  /**
   * Source dataset creator id carried from the discovery/search grid. Lets the
   * "save this creator" action add the creator to the wishlist so the backend
   * provisions its identity and the card resolves.
   */
  /**
   * Authorization opened in the PUBLICITE detail. Its own key rather than a
   * reuse of `contactCollab`: the rail seeds that one with keyword values
   * ("ads"), so the two id spaces would collide.
   */
  contactAd: parseAsString,
  /** The composer occupies the detail panel, one level below `contactAd`. */
  contactAdCompose: parseAsBoolean.withDefault(false),
  /** Discovery creator the drawer was opened from, when there was one. */
  contactSourceCreatorId: parseAsString,
} as const;

/**
 * Every drill-in level of the drawer, cleared together.
 *
 * Selecting a profile, a collaboration, the outreach composer or another
 * platform all mean the same thing: leave whatever was drilled into. Spelling
 * the list at each of those five call sites is how one of them ends up missing
 * a level and reopening on somebody else's authorization.
 */
export const CONTACT_DRILLDOWN_RESET = {
  contactCollab: null,
  contactDeliverable: null,
  contactAd: null,
  contactAdCompose: null,
} as const;

export interface OpenContactParams {
  contactId: string;
  entry?: ContactEntry | null;
  view?: ContactView;
  platform?: ContactPlatform;
  collab?: string | null;
  deliverable?: string | null;
  sourceCreatorId?: string | null;
}

/**
 * Partial nuqs update that opens the drawer on `contactId` and seeds the entry
 * discriminant / view / platform / collab. Pass to a `useQueryStates` setter.
 * Defaults match the parser defaults so callers can open with just an id.
 */
export const openContactParams = ({
  contactId,
  entry = null,
  view = 'profile',
  platform = 'ig',
  collab = null,
  deliverable = null,
  sourceCreatorId = null,
}: OpenContactParams) => ({
  contactId,
  contactEntry: entry,
  contactView: view,
  contactPlatform: platform,
  contactCollab: collab,
  contactDeliverable: deliverable,
  contactSourceCreatorId: sourceCreatorId,
});

/**
 * Nulls out every `contact`-prefixed key so the drawer closes and unmounts
 * cleanly. Pass to a `useQueryStates` setter.
 */
export const clearContactParams = () => ({
  contactId: null,
  contactEntry: null,
  contactView: null,
  contactPlatform: null,
  ...CONTACT_DRILLDOWN_RESET,
  contactSourceCreatorId: null,
});
